import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from '../../context/AuthContext';
import DesktopNavbar from './DesktopNavbar';
import MobileNavbar from './MobileNavbar';
import { motion, AnimatePresence } from "framer-motion";
import { Bell, LogOut, LayoutDashboard, User } from "lucide-react";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isVolunteerDropdownOpen, setIsVolunteerDropdownOpen] = useState(false);
  const { user, logout } = useAuth();
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  // Mock notifications - replace with real data
  useEffect(() => {
    setNotifications([
      { id: 1, title: "Emergency Blood Request", message: "New urgent request in your area", time: "2 min ago", type: "emergency" },
      { id: 2, title: "Donation Reminder", message: "You're eligible to donate again", time: "1 hour ago", type: "reminder" },
      { id: 3, title: "Campaign Update", message: "New NGO campaign launched", time: "3 hours ago", type: "campaign" }
    ]);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 20);

      // Hide/show navbar on scroll
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleRegisterClick = () => {
    navigate('/register');
    setIsOpen(false);
  };

  const handleHospitalClick = (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/hospital/login');
    } else {
      navigate('/hospital/dashboard');
    }
    setIsOpen(false);
    setIsVolunteerDropdownOpen(false);
  };

  const handleDashboardClick = () => {
    if (user && user.role === 'requestor') {
      navigate('/requester-dashboard');
    } else {
      navigate('/dashboard');
    }
    setIsOpen(false);
  };

  const handleLogoutClick = async () => {
    await logout();
    setIsOpen(false);
  };

  const unreadNotifications = notifications.filter(n => n.type === "emergency").length;

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  const volunteerLinks = [
    { name: "NGO's", path: "/ngo-registration" },
    { name: "NGO Login", path: "/ngo/login" },
    { name: "Hospital/Organization", path: "/register-hospital", onClick: handleHospitalClick },
    { name: "Hospital Login", path: "/hospital/login" },
  ];

  const vehicleLinks = [
    // For all users
    { name: "Book Emergency Vehicle", path: "/vehicle/book-vehicle", description: "Request emergency transportation", icon: "car" },
    { name: "Track Your Booking", path: "/vehicle/check-booking", description: "Check status of your booking", icon: "search" },

    // For vehicle owners
    { name: "Driver Login", path: "/vehicle/login", description: "Login for vehicle owners", icon: "login", group: "owner" },
    { name: "Register as Driver", path: "/vehicle/register", description: "Register your vehicle", icon: "user-plus", group: "owner" },
    { name: "Driver Dashboard", path: "/vehicle/dashboard", description: "Manage your vehicle bookings", icon: "dashboard", group: "owner" },
  ];

  const handleMobileSubmenuClick = () => {
    setMobileSubmenuOpen(!mobileSubmenuOpen);
  };

  const handleMobileLinkClick = (link) => {
    if (link.onClick) {
      link.onClick();
    }
    setIsOpen(false);
    setMobileSubmenuOpen(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.nav
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          exit={{ y: -100 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className={`fixed w-full z-50 transition-all duration-500 ${isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-xl border-b border-gray-200'
            : 'bg-white border-b border-gray-100'
            }`}
        >
          {/* Top accent bar */}
          <div className="h-1 bg-gradient-to-r from-red-500 via-red-600 to-red-700" />

          <div className="flex justify-between items-center h-12 sm:h-14 px-4 sm:px-6 lg:px-8">
            {/* Logo Section - Fixed to Left Corner */}
            <div className="flex-shrink-0">
              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <Link to="/" className="flex items-center group">
                  <div className="flex items-center space-x-4">
                    {/* Logo Image */}
                    <div className="relative">
                      <img
                        src="/images/logo1.png"
                        alt="BloodHero Logo"
                        className="h-7 w-auto sm:h-9 md:h-10 transition-all duration-300 group-hover:scale-105"
                      />
                    </div>

                    {/* Logo Text */}
                    <div className="flex flex-col">
                      <div className="flex items-center space-x-1">
                        <span className="text-blue-600 font-bold text-sm sm:text-base">THE</span>
                        <span className="text-red-600 font-bold text-sm sm:text-base flex items-center">
                          BLOOD
                          <span className="text-red-500 mx-1">●</span>
                          <span className="text-red-500">●</span>
                        </span>
                        <span className="text-blue-600 font-bold text-sm sm:text-base">Hero</span>
                      </div>
                      <div className="text-xs text-gray-500 font-medium">Saving Lives Together</div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            </div>

            {/* Desktop Navigation - Centered */}
            <div className="hidden lg:flex items-center flex-1 justify-center">
              <DesktopNavbar
                isActivePath={isActivePath}
                isVolunteerDropdownOpen={isVolunteerDropdownOpen}
                setIsVolunteerDropdownOpen={setIsVolunteerDropdownOpen}
                volunteerLinks={volunteerLinks}
                handleRegisterClick={handleRegisterClick}
                user={user}
                handleLogoutClick={handleLogoutClick}
                handleDashboardClick={handleDashboardClick}
                vehicleLinks={vehicleLinks}
              />
            </div>

            {/* Right Side - Actions & User */}
            <div className="hidden lg:flex items-center space-x-4 flex-shrink-0">
              {/* Notifications */}
              {user && (
                <div className="relative">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="p-1.5 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all duration-200 border border-gray-200 hover:border-gray-300 relative"
                  >
                    <Bell className="w-3.5 h-3.5 text-gray-600" />
                    {unreadNotifications > 0 && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium"
                      >
                        {unreadNotifications}
                      </motion.span>
                    )}
                  </motion.button>

                  {/* Notifications Panel */}
                  <AnimatePresence>
                    {showNotifications && (
                      <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        className="absolute right-0 mt-3 w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 overflow-hidden"
                      >
                        <div className="p-4 border-b border-gray-100">
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                            <button className="text-xs text-red-600 hover:text-red-700 font-medium">Mark all read</button>
                          </div>
                        </div>
                        <div className="max-h-80 overflow-y-auto">
                          {notifications.map((notification) => (
                            <div
                              key={notification.id}
                              className={`p-4 border-l-4 hover:bg-gray-50 transition-colors ${notification.type === 'emergency'
                                ? 'border-red-500 bg-red-50/50'
                                : notification.type === 'reminder'
                                  ? 'border-blue-500 bg-blue-50/50'
                                  : 'border-green-500 bg-green-50/50'
                                }`}
                            >
                              <div className="flex items-start space-x-3">
                                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${notification.type === 'emergency' ? 'bg-red-500' :
                                  notification.type === 'reminder' ? 'bg-blue-500' : 'bg-green-500'
                                  }`} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{notification.title}</p>
                                  <p className="text-xs text-gray-600 mt-1">{notification.message}</p>
                                  <p className="text-xs text-gray-400 mt-2">{notification.time}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* User Actions */}
              <div className="flex items-center space-x-3">
                {user ? (
                  <>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleDashboardClick}
                      className="flex items-center px-3.5 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md text-sm"
                    >
                      <LayoutDashboard className="mr-1.5 h-4 w-4" />
                      Dashboard
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      as={Link}
                      to="/user-profile"
                      className="flex items-center px-3.5 py-2 rounded-xl border-2 border-gray-200 text-gray-700 hover:border-blue-300 hover:text-blue-600 transition-all duration-200 font-medium text-sm"
                    >
                      <User className="mr-1.5 h-4 w-4" />
                      Profile
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleLogoutClick}
                      className="flex items-center px-3.5 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md text-sm"
                    >
                      <LogOut className="mr-1.5 h-4 w-4" />
                      Logout
                    </motion.button>
                  </>
                ) : (
                  <>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Link
                        to="/login"
                        className="inline-block px-4 py-2 rounded-xl border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-all duration-200 font-medium text-sm"
                      >
                        Login
                      </Link>
                    </motion.div>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleRegisterClick}
                      className="px-4 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md text-sm"
                    >
                      Register
                    </motion.button>
                  </>
                )}
              </div>
            </div>

            {/* Mobile Navigation */}
            <MobileNavbar
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              isActivePath={isActivePath}
              mobileSubmenuOpen={mobileSubmenuOpen}
              handleMobileSubmenuClick={handleMobileSubmenuClick}
              volunteerLinks={volunteerLinks}
              vehicleLinks={vehicleLinks}
              handleMobileLinkClick={handleMobileLinkClick}
              handleRegisterClick={handleRegisterClick}
              user={user}
              handleLogoutClick={handleLogoutClick}
              handleDashboardClick={handleDashboardClick}
            />
          </div>



          {/* Bottom accent line */}
          {isScrolled && (
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              className="h-0.5 bg-gradient-to-r from-red-500 via-red-600 to-red-700"
            />
          )}
        </motion.nav>
      )}
    </AnimatePresence>
  );
};

export default Navbar;